import {
  dynamicPageCustomRouterCreatePageClicked,
  presetCollectionCreated,
  presetInstallationResult,
  presetDynamicPageCreated,
  presetPageCreated,
} from '@wix/bi-logger-platform/v2'
import { PAGE_TYPES } from '@wix/wix-data-client-common'
import { cmCollectionCreated } from '@wix/bi-logger-platform-cm/v2'
import { serializePermissions } from '@wix/wix-data-client-common-standalone'
import type { Logger } from '@wix/wix-data-client-common-standalone'
import type { CreateOrConnectDPState } from '../../../../types'
import type { PresetDefinition } from '../../../presets/types'
import type { DpBiDefaults } from '@wix/wix-data-client-common'
import type { Permissions } from '@wix/wix-data-schema-types'

export const getEventsLogger = ({
  logger,
  biDefaults,
  isSandboxEnabled,
  defaultDraftPublishStatus,
}: {
  logger: Logger
  biDefaults: DpBiDefaults
  isSandboxEnabled?: boolean
  defaultDraftPublishStatus: string
}) => {
  return {
    logForCollection: ({
      permissions,
      collectionId,
      collectionName,
      collectionType,
    }: {
      permissions: Permissions
      collectionId: string
      collectionName: string
      collectionType: string
    }) =>
      logger.bi(
        cmCollectionCreated({
          collection_name: collectionName,
          collection_id: collectionId,
          collection_type: collectionType,
          permissions: serializePermissions(permissions),
          default_content: true,
          is_draft_publish_enabled: true,
          default_new_item_status: defaultDraftPublishStatus,
          origin: biDefaults.origin,
          environment: biDefaults.environment,
          is_first_data_modal: biDefaults.is_first_data_modal,
        }),
      ),

    logForAddedPages: ({
      state,
      collectionId,
      collectionName,
      newCollection,
      isEditPage = false,
    }: {
      state: CreateOrConnectDPState
      collectionId: string
      collectionName: string
      newCollection: boolean
      isEditPage?: boolean
    }) => {
      state.pages.forEach(
        ({ pageRef, pageType, pageUrl, isAutoBound, wasStatic }) => {
          logger.bi(
            dynamicPageCustomRouterCreatePageClicked({
              page_type: isEditPage ? 'edit_item' : pageType,
              collection_id: collectionId,
              collection_name: collectionName,
              new_url: pageUrl,
              is_auto_bound: isAutoBound,
              new_collection: newCollection,
              pageId: pageRef.id,
              action_type: wasStatic
                ? 'convert_static_to_dynamic'
                : 'add_dynamic_page',
              ...biDefaults,
            }),
          )
        },
      )
    },
    logForPresetCollection: ({
      preset,
      collectionId,
      collectionName,
      collectionType,
      numberOfFields,
      numberOfRows,
    }: {
      preset: PresetDefinition
      collectionId: string
      collectionName: string
      collectionType: string
      numberOfFields: number
      numberOfRows: number
    }) =>
      logger.bi(
        presetCollectionCreated({
          collection_type: collectionType,
          collection_id: collectionId,
          collection_name: collectionName,
          num_of_fields: numberOfFields,
          num_of_rows: numberOfRows,
          preset_id: preset.biInfo.presetId,
          preset_name: preset.biInfo.presetName,
          is_sandbox_enabled: isSandboxEnabled,
          is_draft_publish_enabled: true,
          default_new_item_status: defaultDraftPublishStatus,
          ...biDefaults,
        }),
      ),
    logForAddedPreset: ({
      state,
      preset,
      collectionId,
      collectionName,
      startTime,
    }: {
      state: CreateOrConnectDPState
      preset: PresetDefinition
      collectionId: string
      collectionName: string
      startTime: number
    }) => {
      state.pages.forEach(({ pageRef, pageType, pageTitle }) => {
        logger.bi(
          pageType === PAGE_TYPES.ITEM
            ? presetDynamicPageCreated({
                page_name: pageTitle,
                pageId: pageRef.id,
                preset_id: preset.biInfo.presetId,
                collection_id: collectionId,
                collection_name: collectionName,
                ...biDefaults,
              })
            : presetPageCreated({
                page_name: pageTitle,
                pageId: pageRef.id,
                preset_id: preset.biInfo.presetId,
                collection_id: collectionId,
                collection_name: collectionName,
                ...biDefaults,
              }),
        )
      })

      const endTime = Date.now()

      if (state.pages.length > 0) {
        logger.bi(
          presetInstallationResult({
            preset_id: preset.biInfo.presetId,
            preset_name: preset.biInfo.presetName,
            time_to_complete: endTime - startTime,
            collection_id: collectionId,
            collection_name: collectionName,
            ...biDefaults,
          }),
        )
      }
    },
  }
}
